import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe, Workflow } from "lucide-react";

const WEBSITE_BULLETS = [
  "Homepage and landing page builds",
  "Conversion-focused copy",
  "Mobile-first structure",
  "Intake or booking handoff",
  "Built for credibility and lead capture",
];

const REVENUE_BULLETS = [
  "Lead capture and routing",
  "CRM pipeline setup",
  "Follow-up automation",
  "Booking and payment flow",
  "Review and reactivation systems",
];

/**
 * Two-path section on the homepage — routes the visitor toward either the
 * Websites division or the Revenue Systems division. Both paths are part of
 * one revenue system.
 */
export default function TwoPath() {
  return (
    <section
      data-testid="two-path-section"
      className="bg-sand-100 px-5 sm:px-6 md:px-10 py-16 sm:py-20 md:py-24"
    >
      <div className="max-w-7xl mx-auto">
        {/* threshold marker — the qualification gate crossed after the audit */}
        <div className="reveal flex items-center gap-3 mb-10 sm:mb-12">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay-deep whitespace-nowrap">
            Threshold · The Build
          </span>
          <span className="h-px flex-1 bg-sand-300" />
        </div>
        <div className="grid lg:grid-cols-12 gap-5 sm:gap-6 lg:gap-8 items-end mb-10 sm:mb-12 lg:mb-14">
          <div className="lg:col-span-7 reveal">
            <p className="eyebrow">After The Audit</p>
            <h2
              data-kinetic
              className="font-display mt-4 sm:mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3.2rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink"
            >
              After the audit, we build{" "}
              <span className="italic text-ink-muted font-normal">
                what the leak requires.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5 reveal">
            <p className="text-base md:text-lg text-ink-muted leading-relaxed">
              Website Builds and Revenue Systems are the two build paths. The
              audit tells us which one your leak actually requires.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <PathCard
            testId="path-card-websites"
            tag="Modern Exit Websites"
            icon={Globe}
            heading="Website Builds."
            body="For businesses whose offer, trust, booking path, or online presence is unclear."
            bullets={WEBSITE_BULLETS}
            ctaLabel="See the Build Options"
            to="/packages"
            tone="ivory"
          />
          <PathCard
            testId="path-card-revenue"
            tag="Modern Exit Revenue Systems"
            icon={Workflow}
            heading="Revenue Systems."
            body="For businesses losing leads after the first inquiry because the CRM, booking, follow-up, payment, or reporting path is broken."
            bullets={REVENUE_BULLETS}
            ctaLabel="Find the Leak"
            to="/revenue-leak-audit"
            tone="dark"
          />
        </div>
      </div>
    </section>
  );
}

function PathCard({ testId, tag, icon: Icon, heading, body, bullets, ctaLabel, to, tone }) {
  const isDark = tone === "dark";
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`reveal group relative rounded-3xl border p-7 sm:p-9 md:p-11 flex flex-col min-h-[420px] sm:min-h-[460px] overflow-hidden transition-all hover:-translate-y-0.5 ${
        isDark
          ? "bg-ink-deep border-ink-deep text-sand-100"
          : "bg-sand-50 border-sand-300 text-ink"
      }`}
    >
      {/* Soft warm wash on dark card */}
      {isDark && (
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(560px 320px at 12% 20%, rgba(165,106,67,0.45), transparent 60%), radial-gradient(560px 320px at 90% 80%, rgba(111,115,94,0.22), transparent 60%)",
          }}
        />
      )}

      <div className="relative flex items-center justify-between">
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${
            isDark
              ? "bg-white/8 text-sand-300 border border-white/10"
              : "bg-sand-100 text-clay border border-sand-300"
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-clay" />
          {tag}
        </span>
        <span
          className={`h-11 w-11 grid place-items-center rounded-2xl ${
            isDark
              ? "bg-white/8 border border-white/10 text-sand-100"
              : "bg-sand-100 border border-sand-300 text-ink"
          }`}
        >
          <Icon size={18} strokeWidth={1.6} />
        </span>
      </div>

      <h3
        className={`relative font-display mt-8 sm:mt-10 text-2xl sm:text-3xl md:text-[2.2rem] leading-[1.1] tracking-tight font-semibold ${
          isDark ? "text-sand-100" : "text-ink"
        }`}
      >
        {heading}
      </h3>
      <p
        className={`relative mt-4 text-[14.5px] sm:text-[15px] md:text-base leading-relaxed max-w-md ${
          isDark ? "text-sand-300/85" : "text-ink-muted"
        }`}
      >
        {body}
      </p>

      <ul className="relative mt-6 space-y-2.5">
        {bullets.map((b) => (
          <li
            key={b}
            className={`flex items-start gap-2.5 text-[13.5px] sm:text-[14px] leading-snug ${
              isDark ? "text-sand-300/90" : "text-ink-muted"
            }`}
          >
            <span
              className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                isDark ? "bg-clay" : "bg-olive"
              }`}
            />
            {b}
          </li>
        ))}
      </ul>

      <div className="relative mt-auto pt-8 sm:pt-10 flex items-center justify-between">
        <span
          className={`inline-flex items-center gap-2 text-[14px] font-medium ${
            isDark ? "text-sand-100" : "text-ink"
          }`}
        >
          {ctaLabel}
          <ArrowUpRight
            size={16}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </span>
        <span
          className={`font-mono text-[10px] uppercase tracking-[0.18em] ${
            isDark ? "text-sand-300/70" : "text-ink-muted"
          }`}
        >
          {isDark ? "· revenue systems ·" : "· front-end site ·"}
        </span>
      </div>
    </Link>
  );
}
